// src/pages/NhanVienPage.js
import React, { useState, useEffect, useMemo } from 'react';
import { db, auth, app } from '../firebase';
import {
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  getAuth as getTempAuth,
  signOut as signOutTemp,
} from 'firebase/auth';
import { initializeApp, deleteApp } from 'firebase/app';
import {
  collection,
  onSnapshot,
  doc,
  deleteDoc,
  query,
  where,
  getDocs,
  limit,
  addDoc,
  updateDoc,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Button,
  Paper,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Chip,
  Tooltip,
  Stack,
  TablePagination,
  TextField,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LockResetIcon from '@mui/icons-material/LockReset';

import CustomerDialog from '../components/CustomerDialog';
import SimpleEntityDialog from '../components/SimpleEntityDialog';
import SupplierDialog from '../components/SupplierDialog';
import EmployeeDialog from '../components/EmployeeDialog';
import RoleDialog from '../components/RoleDialog';
import DriverDialog from '../components/DriverDialog';

const formatDate = (timestamp) => {
  if (!timestamp) return 'N/A';
  return new Date(timestamp.seconds * 1000).toLocaleDateString('vi-VN');
};

function NhanVienPage() {
  const [tabIndex, setTabIndex] = useState(0);
  const [customers, setCustomers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [assistantDrivers, setAssistantDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [roles, setRoles] = useState([]);
  const [currentItem, setCurrentItem] = useState(null);
  const [currentDialog, setCurrentDialog] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    setSearchTerm('');
    setPage(0);
    setRowsPerPage(10);
  }, [tabIndex]);

  useEffect(() => {
    const collectionsToWatch = {
      customers: setCustomers,
      suppliers: setSuppliers,
      drivers: setDrivers,
      assistantDrivers: setAssistantDrivers,
      vehicles: setVehicles,
      warehouses: setWarehouses,
      employees: setEmployees,
      roles: setRoles,
    };
    const unsubs = Object.entries(collectionsToWatch).map(([name, setter]) =>
      onSnapshot(collection(db, name), (snapshot) =>
        setter(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })))
      )
    );
    return () => unsubs.forEach((unsub) => unsub());
  }, []);

  const handleOpenDialog = (dialogName, itemToEdit = null) => {
    setCurrentItem(itemToEdit);
    setCurrentDialog(dialogName);
  };

  const handleCloseDialog = () => {
    setCurrentItem(null);
    setCurrentDialog(null);
  };

  const handleSave = async (collectionName, data) => {
    const listMap = {
      customers,
      suppliers,
      drivers,
      assistantDrivers,
      vehicles,
      warehouses,
      roles,
    };
    const listToCheck = listMap[collectionName];
    const checkField = 'name';
    const trimmedValue = data[checkField]?.trim();

    if (!trimmedValue) {
      return alert('Tên/trường chính không được để trống.');
    }

    const isDuplicate = listToCheck.some(
      (item) =>
        item.name?.trim().toLowerCase() === trimmedValue.toLowerCase() &&
        item.id !== currentItem?.id
    );
    if (isDuplicate) {
      return alert(`"${trimmedValue}" đã tồn tại.`);
    }

    if (currentItem) {
      const docRef = doc(db, collectionName, currentItem.id);
      await updateDoc(docRef, data);
      alert('Cập nhật thành công!');
    } else {
      await addDoc(collection(db, collectionName), {
        ...data,
        name: trimmedValue,
        createdAt: serverTimestamp(),
      });
      alert('Tạo mới thành công!');
    }
    handleCloseDialog();
  };

  const handleSaveEmployee = async (employeeData) => {
    const trimmedUsername = employeeData.username?.trim();
    if (!trimmedUsername || !employeeData.fullName?.trim()) {
      return alert('Họ tên và Username (Email) không được để trống.');
    }

    if (currentItem) {
      const { password, ...dataToUpdate } = employeeData;
      const docRef = doc(db, 'employees', currentItem.id);
      await updateDoc(docRef, dataToUpdate);
      alert('Cập nhật nhân viên thành công!');
    } else {
      if (!employeeData.password) {
        return alert('Vui lòng nhập mật khẩu cho nhân viên mới.');
      }
      // ❗Dùng app.options thay cho firebaseConfig
      const tempAppName = `temp-user-creation-${Date.now()}`;
      const tempApp = initializeApp(app.options, tempAppName);
      const tempAuth = getTempAuth(tempApp);
      try {
        const isDuplicate =
          employees.some((e) => e.username.toLowerCase() === trimmedUsername.toLowerCase()) ||
          drivers.some((d) => d.username?.toLowerCase() === trimmedUsername.toLowerCase());
        if (isDuplicate) {
          throw new Error('Email này đã được sử dụng.');
        }
        const userCredential = await createUserWithEmailAndPassword(
          tempAuth,
          trimmedUsername,
          employeeData.password
        );
        const newUserId = userCredential.user.uid;
        await signOutTemp(tempAuth);
        const { password, ...dataToSave } = employeeData;
        await setDoc(doc(db, 'employees', newUserId), {
          ...dataToSave,
          username: trimmedUsername,
          uid: newUserId,
          createdAt: serverTimestamp(),
        });
        alert('Tạo nhân viên mới thành công!');
      } catch (error) {
        if (error.code === 'auth/email-already-in-use' || error.message === 'Email này đã được sử dụng.') {
          alert('Lỗi: Email này đã được sử dụng. Vui lòng chọn email khác.');
        } else if (error.code === 'auth/weak-password') {
          alert('Lỗi: Mật khẩu quá yếu. Mật khẩu phải có ít nhất 6 ký tự.');
        } else {
          console.error('Lỗi khi tạo nhân viên:', error);
          alert('Đã có lỗi xảy ra khi tạo nhân viên.');
        }
      } finally {
        // giải phóng app tạm
        try {
          await deleteApp(tempApp);
        } catch (_) {}
      }
    }
    handleCloseDialog();
  };

  const handleSaveDriver = async (driverData) => {
    const trimmedUsername = driverData.username?.trim();
    if (!trimmedUsername || !driverData.name?.trim()) {
      return alert('Tên tài xế và ID đăng nhập (Email) không được để trống.');
    }

    if (currentItem) {
      const usernameChanged = trimmedUsername !== currentItem.username;
      const isAddingLogin = !currentItem.username && driverData.username;

      if (usernameChanged) {
        const isDuplicate =
          employees.some((e) => e.username.toLowerCase() === trimmedUsername.toLowerCase()) ||
          drivers.some(
            (d) => d.username?.toLowerCase() === trimmedUsername.toLowerCase() && d.id !== currentItem.id
          );
        if (isDuplicate) {
          return alert('Lỗi: Email này đã được sử dụng bởi một người dùng khác.');
        }
      }

      if (isAddingLogin) {
        if (!driverData.password) {
          return alert('Vui lòng nhập mật khẩu.');
        }
        const tempAppName = `temp-driver-creation-${Date.now()}`;
        const tempApp = initializeApp(app.options, tempAppName);
        const tempAuth = getTempAuth(tempApp);
        try {
          const userCredential = await createUserWithEmailAndPassword(
            tempAuth,
            trimmedUsername,
            driverData.password
          );
          const newUserId = userCredential.user.uid;
          await signOutTemp(tempAuth);
          const { password, ...dataToUpdate } = driverData;
          const docRef = doc(db, 'drivers', currentItem.id);
          await updateDoc(docRef, { ...dataToUpdate, uid: newUserId });
          alert('Cập nhật và tạo tài khoản cho tài xế thành công!');
        } catch (error) {
          if (error.code === 'auth/email-already-in-use') {
            alert('Lỗi: Email này đã được sử dụng.');
          } else if (error.code === 'auth/weak-password') {
            alert('Lỗi: Mật khẩu phải có ít nhất 6 ký tự.');
          } else {
            console.error('Lỗi khi tạo tài khoản cho tài xế:', error);
            alert('Đã có lỗi xảy ra.');
          }
          return;
        } finally {
          try {
            await deleteApp(tempApp);
          } catch (_) {}
        }
      } else {
        const { password, ...dataToUpdate } = driverData;
        const docRef = doc(db, 'drivers', currentItem.id);
        await updateDoc(docRef, dataToUpdate);
        alert('Cập nhật tài xế thành công!');
        if (usernameChanged && currentItem.uid) {
          alert(
            'LƯU Ý QUAN TRỌNG:\n\nBạn đã thay đổi email trong cơ sở dữ liệu, nhưng email ĐĂNG NHẬP thực tế của tài xế KHÔNG THAY ĐỔI.\n\nĐể thay đổi email đăng nhập, cần phải thực hiện qua chức năng quản trị của Firebase.'
          );
        }
      }
    } else {
      if (!driverData.password) {
        return alert('Vui lòng nhập mật khẩu cho tài xế mới.');
      }
      const tempAppName = `temp-driver-creation-${Date.now()}`;
      const tempApp = initializeApp(app.options, tempAppName);
      const tempAuth = getTempAuth(tempApp);
      try {
        const isDuplicate =
          employees.some((e) => e.username.toLowerCase() === trimmedUsername.toLowerCase()) ||
          drivers.some((d) => d.username?.toLowerCase() === trimmedUsername.toLowerCase());
        if (isDuplicate) {
          throw new Error('Email này đã được sử dụng.');
        }
        const userCredential = await createUserWithEmailAndPassword(
          tempAuth,
          trimmedUsername,
          driverData.password
        );
        const newUserId = userCredential.user.uid;
        await signOutTemp(tempAuth);
        const { password, ...dataToSave } = driverData;
        await addDoc(collection(db, 'drivers'), {
          ...dataToSave,
          username: trimmedUsername,
          uid: newUserId,
          createdAt: serverTimestamp(),
        });
        alert('Tạo tài xế mới thành công!');
      } catch (error) {
        if (error.code === 'auth/email-already-in-use' || error.message === 'Email này đã được sử dụng.') {
          alert('Lỗi: Email này đã được sử dụng.');
        } else if (error.code === 'auth/weak-password') {
          alert('Lỗi: Mật khẩu phải có ít nhất 6 ký tự.');
        } else {
          console.error('Lỗi khi tạo tài xế:', error);
          alert('Đã có lỗi xảy ra khi tạo tài xế.');
        }
        return;
      } finally {
        try {
          await deleteApp(tempApp);
        } catch (_) {}
      }
    }
    handleCloseDialog();
  };

  const handleDelete = async (collectionName, docId, name, usageCheck) => {
    if (window.confirm(`Bạn có chắc muốn xóa "${name}"? Hành động này không thể hoàn tác.`)) {
      try {
        if (usageCheck) {
          const usageQuery = query(
            collection(db, usageCheck.in),
            where(usageCheck.field, '==', docId),
            limit(1)
          );
        const usageSnap = await getDocs(usageQuery);
          if (!usageSnap.empty) {
            return alert(
              `Không thể xóa "${name}" vì đang được sử dụng trong phiếu ${
                usageCheck.in === 'imports' ? 'nhập' : 'xuất'
              }.`
            );
          }
        }
        await deleteDoc(doc(db, collectionName, docId));
        alert('Xóa thành công!');
      } catch (e) {
        console.error('Lỗi khi xóa:', e);
        alert('Đã có lỗi xảy ra khi xóa.');
      }
    }
  };

  const handleResetPassword = async (email) => {
    if (!email) {
      alert('Người dùng này chưa có email để đặt lại mật khẩu.');
      return;
    }
    if (window.confirm(`Bạn có chắc muốn gửi email đặt lại mật khẩu cho ${email}?`)) {
      try {
        await sendPasswordResetEmail(auth, email);
        alert(`Đã gửi email đặt lại mật khẩu thành công tới ${email}.`);
      } catch (error) {
        console.error('Lỗi khi gửi email đặt lại mật khẩu:', error);
        alert('Có lỗi xảy ra, không thể gửi email.');
      }
    }
  };

  const lists = [
    { name: 'employee', title: 'Hồ sơ Nhân viên', items: employees, collectionName: 'employees' },
    { name: 'role', title: 'Quản lý Vai trò', items: roles, collectionName: 'roles' },
    { name: 'customer', title: 'Khách hàng', items: customers, collectionName: 'customers' },
    {
      name: 'supplier',
      title: 'Nhà cung cấp',
      items: suppliers,
      collectionName: 'suppliers',
      usageCheck: { in: 'imports', field: 'supplierId' },
    },
    {
      name: 'driver',
      title: 'Tài xế',
      items: drivers,
      collectionName: 'drivers',
      usageCheck: { in: 'exports', field: 'driverId' },
    },
    {
      name: 'assistantDriver',
      title: 'Phụ xe',
      items: assistantDrivers,
      collectionName: 'assistantDrivers',
      usageCheck: { in: 'exports', field: 'assistantDriverId' },
    },
    {
      name: 'vehicle',
      title: 'Xe',
      items: vehicles,
      collectionName: 'vehicles',
      usageCheck: { in: 'exports', field: 'vehicleId' },
    },
    {
      name: 'warehouse',
      title: 'Kho',
      items: warehouses,
      collectionName: 'warehouses',
      usageCheck: { in: 'exports', field: 'warehouseId' },
    },
  ];

  const activeList = lists[tabIndex];

  // === TÌM KIẾM: thêm businessCode và representativePersonalId ===
  const processedList = useMemo(() => {
    if (!activeList.items) return [];
    return activeList.items.filter((item) => {
      const term = searchTerm.toLowerCase();
      return (
        item.name?.toLowerCase().includes(term) ||
        item.fullName?.toLowerCase().includes(term) ||
        item.username?.toLowerCase().includes(term) ||
        item.phone?.toLowerCase().includes(term) ||
        item.address?.toLowerCase().includes(term) ||
        item.code?.toLowerCase().includes(term) || // Mã số thuế (MST)
        item.businessCode?.toLowerCase().includes(term) || // Mã số kinh doanh (MỚI)
        item.representativePersonalId?.toLowerCase().includes(term) || // CMND/CCCD đại diện (MỚI)
        item.position?.toLowerCase().includes(term) ||
        item.representativePosition?.toLowerCase().includes(term) ||
        item.chucVu?.toLowerCase().includes(term)
      );
    });
  }, [activeList, searchTerm]);

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const renderActiveList = () => {
    const listProps = {
      onEdit: (item) => handleOpenDialog(activeList.name, item),
      onDelete: (id, name) =>
        handleDelete(activeList.collectionName, id, name, activeList.usageCheck),
    };
    const listToRender = processedList.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );

    switch (activeList.name) {
      // ===== KHÁCH HÀNG =====
      case 'customer': {
        const headCellsCustomer = [
          { id: 'name', label: 'Tên' },
          { id: 'code', label: 'Mã (MST)' },
          { id: 'businessCode', label: 'Mã số kinh doanh' },
          { id: 'representative', label: 'Người đại diện' },
          { id: 'position', label: 'Chức vụ' },
          { id: 'representativePersonalId', label: 'CMND/CCCD (Đại diện)' },
          { id: 'phone', label: 'Số điện thoại' },
          { id: 'email', label: 'Email' },
          { id: 'address', label: 'Địa chỉ' },
          { id: 'note', label: 'Ghi chú' },
        ];

        return (
          <Paper>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {headCellsCustomer.map((cell) => (
                      <TableCell key={cell.id} sx={{ fontWeight: 'bold' }}>
                        {cell.label}
                      </TableCell>
                    ))}
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      Thao tác
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {listToRender.map((item) => (
                    <TableRow key={item.id} hover>
                      <TableCell>{item.name}</TableCell>
                      <TableCell>{item.code}</TableCell>
                      <TableCell>{item.businessCode || ''}</TableCell>
                      <TableCell>{item.representative}</TableCell>
                      <TableCell>
                        {item.position || item.representativePosition || item.chucVu || ''}
                      </TableCell>
                      <TableCell>{item.representativePersonalId || ''}</TableCell>
                      <TableCell>{item.phone}</TableCell>
                      <TableCell>{item.email}</TableCell>
                      <TableCell>{item.address}</TableCell>
                      <TableCell>{item.note || ''}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => listProps.onEdit(item)}
                          color="warning"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => listProps.onDelete(item.id, item.name)}
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={processedList.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
        );
      }

      // ===== NHÀ CUNG CẤP =====
      case 'supplier': {
        const headCellsSupplier = [
          { id: 'name', label: 'Tên' },
          { id: 'code', label: 'Mã (MST)' },
          { id: 'representative', label: 'Người đại diện' },
          { id: 'phone', label: 'Số điện thoại' },
          { id: 'email', label: 'Email' },
          { id: 'address', label: 'Địa chỉ' },
          { id: 'note', label: 'Ghi chú' },
        ];
        return (
          <Paper>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {headCellsSupplier.map((cell) => (
                      <TableCell key={cell.id} sx={{ fontWeight: 'bold' }}>
                        {cell.label}
                      </TableCell>
                    ))}
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      Thao tác
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {listToRender.map((item) => (
                    <TableRow key={item.id} hover>
                      <TableCell>{item.name}</TableCell>
                      <TableCell>{item.code}</TableCell>
                      <TableCell>{item.representative}</TableCell>
                      <TableCell>{item.phone}</TableCell>
                      <TableCell>{item.email}</TableCell>
                      <TableCell>{item.address}</TableCell>
                      <TableCell>{item.note || ''}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => listProps.onEdit(item)}
                          color="warning"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => listProps.onDelete(item.id, item.name)}
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={processedList.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
        );
      }

      // ===== NHÂN VIÊN =====
      case 'employee': {
        const headCellsEmployee = [
          { id: 'fullName', label: 'Họ tên' },
          { id: 'username', label: 'Username (Email)' },
          { id: 'roles', label: 'Chức vụ (Vai trò)' },
          { id: 'department', label: 'Phòng ban' },
          { id: 'createdAt', label: 'Ngày tạo' },
        ];
        return (
          <Paper>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {headCellsEmployee.map((cell) => (
                      <TableCell key={cell.id} sx={{ fontWeight: 'bold' }}>
                        {cell.label}
                      </TableCell>
                    ))}
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      Thao tác
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {listToRender.map((item) => (
                    <TableRow key={item.id} hover>
                      <TableCell>{item.fullName}</TableCell>
                      <TableCell>{item.username}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                          {(item.roles || []).map((roleId) => {
                            const role = roles.find((r) => r.id === roleId);
                            return (
                              <Chip key={roleId} label={role?.name || roleId} size="small" variant="outlined" />
                            );
                          })}
                        </Stack>
                      </TableCell>
                      <TableCell>{item.department}</TableCell>
                      <TableCell>{formatDate(item.createdAt)}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Gửi email Reset Mật khẩu">
                          <IconButton
                            size="small"
                            onClick={() => handleResetPassword(item.username)}
                            color="primary"
                          >
                            <LockResetIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Chỉnh sửa">
                          <IconButton
                            size="small"
                            onClick={() => listProps.onEdit(item)}
                            color="warning"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Xóa">
                          <IconButton
                            size="small"
                            onClick={() => listProps.onDelete(item.id, item.fullName)}
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={processedList.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
        );
      }

      // ===== TÀI XẾ =====
      case 'driver': {
        const headCellsDriver = [
          { id: 'name', label: 'Tên tài xế' },
          { id: 'username', label: 'ID đăng nhập (Email)' },
        ];
        return (
          <Paper>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {headCellsDriver.map((cell) => (
                      <TableCell key={cell.id} sx={{ fontWeight: 'bold' }}>
                        {cell.label}
                      </TableCell>
                    ))}
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      Thao tác
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {listToRender.map((item) => (
                    <TableRow key={item.id} hover>
                      <TableCell>{item.name}</TableCell>
                      <TableCell>{item.username}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Gửi email Reset Mật khẩu">
                          <IconButton
                            size="small"
                            onClick={() => handleResetPassword(item.username)}
                            color="primary"
                          >
                            <LockResetIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Chỉnh sửa">
                          <IconButton
                            size="small"
                            onClick={() => listProps.onEdit(item)}
                            color="warning"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Xóa">
                          <IconButton
                            size="small"
                            onClick={() => listProps.onDelete(item.id, item.name)}
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={processedList.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
        );
      }

      default:
        return (
          <Paper>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>{activeList.title}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      Thao tác
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {listToRender.map((item) => (
                    <TableRow key={item.id} hover>
                      <TableCell>{item.name}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => listProps.onEdit(item)}
                          color="warning"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => listProps.onDelete(item.id, item.name)}
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={processedList.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
        );
    }
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 2 }}>
        Quản lý Danh mục & Phân quyền
      </Typography>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={tabIndex}
          onChange={(e, newValue) => setTabIndex(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          {lists.map((list) => (
            <Tab label={list.title} key={list.title} />
          ))}
        </Tabs>
      </Box>
      <Box sx={{ pt: 2 }}>
        <Paper sx={{ p: 2, mb: 2 }}>
          <Stack direction="row" spacing={2} justifyContent="space-between">
            <TextField
              label={`Tìm kiếm trong ${activeList.title}...`}
              variant="outlined"
              size="small"
              fullWidth
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Button
              onClick={() => handleOpenDialog(activeList.name)}
              variant="contained"
              sx={{ whiteSpace: 'nowrap' }}
            >
              Thêm {activeList.title}
            </Button>
          </Stack>
        </Paper>
        {renderActiveList()}
      </Box>

      {/* Dialogs */}
      <CustomerDialog
        open={currentDialog === 'customer'}
        onClose={handleCloseDialog}
        onSave={(data) => handleSave('customers', data)}
        initialCustomer={currentItem}
      />
      <SupplierDialog
        open={currentDialog === 'supplier'}
        onClose={handleCloseDialog}
        onSave={(data) => handleSave('suppliers', data)}
        initialSupplier={currentItem}
      />
      <EmployeeDialog
        open={currentDialog === 'employee'}
        onClose={handleCloseDialog}
        onSave={handleSaveEmployee}
        initialEmployee={currentItem}
        roles={roles}
      />
      <RoleDialog
        open={currentDialog === 'role'}
        onClose={handleCloseDialog}
        onSave={(data) => handleSave('roles', data)}
        initialRole={currentItem}
      />
      <DriverDialog
        open={currentDialog === 'driver'}
        onClose={handleCloseDialog}
        onSave={handleSaveDriver}
        initialDriver={currentItem}
      />
      <SimpleEntityDialog
        open={currentDialog === 'assistantDriver'}
        onClose={handleCloseDialog}
        onSave={(data) => handleSave('assistantDrivers', data)}
        title={currentItem ? 'Sửa Phụ Xe' : 'Tạo Phụ Xe Mới'}
        label="Tên phụ xe"
        initialItem={currentItem}
      />
      <SimpleEntityDialog
        open={currentDialog === 'vehicle'}
        onClose={handleCloseDialog}
        onSave={(data) => handleSave('vehicles', data)}
        title={currentItem ? 'Sửa Xe' : 'Tạo Xe Mới'}
        label="Biển số xe"
        initialItem={currentItem}
      />
      <SimpleEntityDialog
        open={currentDialog === 'warehouse'}
        onClose={handleCloseDialog}
        onSave={(data) => handleSave('warehouses', data)}
        title={currentItem ? 'Sửa Kho' : 'Tạo Kho Mới'}
        label="Tên kho"
        initialItem={currentItem}
      />
    </Box>
  );
}

export default NhanVienPage;
